import {
  PaginatedData,
  PaginatedParams,
  FilterParams
} from "src/library/utils";
import {
  EndpointConfig,
  RequestConfig,
  UseApiRequest
} from "src/core/shared/api/config.api";
import useAxios from "src/core/shared/hooks/useAxios";
import {
  IOrder,
  IOrderLine
} from "src/core/orders/api/orders.api";
import { ICustomer } from "src/core/customers/api/customers.api";
import { IProduct } from "src/core/products/api/products.api";
import cacheMinutesToMilliseconds from "src/library/utils/time-helpers/cache-minutes-to-milliseconds";

export type ListCallOffParams = { pagination: PaginatedParams<ICallOff>; filters: FilterParams<ICallOff> };
export type UpdateCallOffParams = {callOffId: number, callOffUpdate: Partial<ICallOff>};
export type ListOrdersByCallOffFilterParams = {filters: FilterParams<IOrder>, pagination: PaginatedParams<IOrder> };

type APIEndpointConfig = {
  CREATE_CALL_OFF: EndpointConfig<
  ICallOffCreate,
  ICallOff
  >;
  GET_CALL_OFF: EndpointConfig<{id: number}, ICallOff >;
  UPDATE_CALL_OFF: EndpointConfig<UpdateCallOffParams, ICallOff>;
  LIST_CALL_OFFS: EndpointConfig<ListCallOffParams, PaginatedData<ICallOff>>
  LIST_CUSTOMER_CALL_OFFS: EndpointConfig<{customerId: number}, ICallOff[]>
  LIST_ORDER_BY_CALL_OFFS: EndpointConfig<ListOrdersByCallOffFilterParams & { callOffId: number }, PaginatedData<IOrder>>
  DELETE_CALL_OFF: EndpointConfig<{callOffId: number}, ICallOff[]>
};

const BASE_URL = `${process.env.REACT_APP_API_URL}/call-off`;
export const LIST_CUSTOMER_CALL_OFFS_CACHE_KEY = "list-customer-call-offs";

const requests: RequestConfig<APIEndpointConfig> = {
  CREATE_CALL_OFF: callOff => ({
    url: `${BASE_URL}/create`,
    method: "POST",
    data: callOff
  }),
  GET_CALL_OFF: params => ({
    url: `${BASE_URL}/${params.id}`,
    method: "GET"
  }),
  UPDATE_CALL_OFF: ({ callOffId, callOffUpdate }) => ({
    url: `${BASE_URL}/update/${callOffId}`,
    method: "POST",
    data: callOffUpdate

  }),
  LIST_CALL_OFFS: params => ({
    url: `${BASE_URL}/all`,
    method: "GET",
    params
  }),
  LIST_CUSTOMER_CALL_OFFS: params => ({
    url: `${BASE_URL}/customer/${params.customerId}`,
    method: "GET",
    params,
    suppressLogsOnCache: true,
    cache: {
      maxAge: cacheMinutesToMilliseconds(5),
      clearOnError: true,
      key: () => `${params.customerId}_${LIST_CUSTOMER_CALL_OFFS_CACHE_KEY}`
    }
  }),
  LIST_ORDER_BY_CALL_OFFS: params => ({
    url: `${BASE_URL}/orders/${params.callOffId}`,
    method: "GET",
    params
  }),
  DELETE_CALL_OFF: params => ({
    url: `${BASE_URL}/${params.callOffId}`,
    method: "DELETE",
    params
  })
};

export const useCallOffApi: UseApiRequest<APIEndpointConfig> = name => useAxios(requests[ name ], name);

export interface ICallOff {
    id: number;
    name: string;
    customerReference?: string;
    customerId: number,
    customer?: ICustomer,
    productId: number,
    product: IProduct,
    orderLines?: IOrderLine[],
    initialQuantity: number,
    currentQuantity: number,
    startDate: Date,
    endDate?: Date,
    deleted?: boolean,
    exclusiveOrdering?: boolean
  }

export type ICallOffCreate = Omit<ICallOff, "id">;
